import React from 'react';
import { Link } from 'react-router-dom';

interface BindPhoneModuleProps {
  phone: string;
  setPhone: (phone: string) => void;
  code: string;
  setCode: (code: string) => void;
  accountType: 'PERSONAL' | 'ENTERPRISE';
  setAccountType: (type: 'PERSONAL' | 'ENTERPRISE') => void;
  agreed: boolean;
  setAgreed: (agreed: boolean) => void;
  status: string;
  isCounting: boolean;
  captchaLoading: boolean;
  captchaLabel: string;
  onSendCodeClick: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onBackToWeChat: () => void;
  onAccountTypeChange?: () => void;
}

export const BindPhoneModule: React.FC<BindPhoneModuleProps> = ({
  phone,
  setPhone,
  code,
  setCode,
  accountType,
  setAccountType,
  agreed,
  setAgreed,
  status,
  isCounting,
  captchaLoading,
  captchaLabel,
  onSendCodeClick,
  onSubmit,
  onBackToWeChat,
  onAccountTypeChange,
}) => {
  return (
    <div className="space-y-6 animate-[fadeIn_0.25s_ease-out]">

      <form onSubmit={onSubmit} className="space-y-5">
        {/* Account Type Switch */}
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1.5">账号身份</label>
          <div className="flex items-center bg-gray-100 rounded-full p-1">
            <button
              type="button"
              onClick={() => {
                setAccountType('PERSONAL');
                onAccountTypeChange?.();
              }}
              className={`flex-1 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
                accountType === 'PERSONAL'
                  ? 'bg-white text-foreground-950 shadow-sm font-semibold'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-user-3-line text-sm"></i>
              个人账号
            </button>
            <button
              type="button"
              onClick={() => {
                setAccountType('ENTERPRISE');
                onAccountTypeChange?.();
              }}
              className={`flex-1 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 ${
                accountType === 'ENTERPRISE'
                  ? 'bg-white text-foreground-950 shadow-sm font-semibold'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-building-2-line text-sm"></i>
              企业账号
            </button>
          </div>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1.5">绑定手机号</label>
          <div className="relative">
            <i className="ri-smartphone-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
              placeholder="请输入要绑定的手机号"
              className="w-full pl-11 pr-4 py-3 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition-all"
            />
          </div>
        </div>

        {/* SMS Code */}
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1.5">短信验证码</label>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <i className="ri-shield-keyhole-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="请输入 6 位验证码"
                className="w-full pl-11 pr-4 py-3 rounded-lg border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition-all"
              />
            </div>
            <button
              type="button"
              onClick={onSendCodeClick}
              disabled={isCounting || captchaLoading || phone.length < 11}
              className="flex-shrink-0 px-4 py-3 rounded-lg border border-emerald-300/80 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap"
            >
              {captchaLoading ? '发送中...' : captchaLabel}
            </button>
          </div>
        </div>

        {/* Agreement */}
        <div className="flex items-start gap-2">
          <button
            type="button"
            onClick={() => setAgreed(!agreed)}
            className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all cursor-pointer ${
              agreed ? 'bg-emerald-600 border-emerald-600' : 'border-background-300'
            }`}
          >
            {agreed && <i className="ri-check-line text-white text-xs"></i>}
          </button>
          <span className="text-xs text-foreground-500 leading-relaxed">
            我已阅读并同意
            <Link to="/terms" className="text-emerald-700 hover:underline mx-1 cursor-pointer font-medium">服务条款</Link>
            和
            <Link to="/privacy" className="text-emerald-700 hover:underline ml-1 cursor-pointer font-medium">隐私政策</Link>
          </span>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={phone.length < 11 || code.length !== 6 || status === 'authenticating'}
          className="w-full py-3 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer whitespace-nowrap shadow-sm"
        >
          {status === 'authenticating' ? '绑定中...' : '确认绑定并完成登录'}
        </button>

        {/* Back button */}
        <button
          type="button"
          onClick={onBackToWeChat}
          className="w-full py-2.5 text-xs text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer text-center flex items-center justify-center gap-1"
        >
          <i className="ri-arrow-left-s-line text-sm"></i>
          返回微信扫码登录
        </button>
      </form>
    </div>
  );
};
